"use client";
import { getProviders, signIn, ClientSafeProvider } from 'next-auth/react';
import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, Link as MuiLink, Divider, CircularProgress } from '@mui/material';
import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import Link from 'next/link';

export default function AuthPage() {
  const [providers, setProviders] = useState<Record<string, ClientSafeProvider> | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    const fetchProviders = async () => {
      const providers = await getProviders();
      setProviders(providers);
    };
    fetchProviders();
  }, []);

  const handleCredentialsSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    if (!email || !password) { setError('Both email and password are required'); setLoading(false); return; }
    const res = await signIn('credentials', { email, password, redirect: false, remember });
    setLoading(false);
    if (res?.error) setError('Invalid email or password');
    else if (res?.ok) window.location.href = '/dashboard';
  };

  return (
    <PublicPageLayout>
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          py: 4,
          px: 2,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight={700} mb={2} color="text.primary">
          Sign In
        </Typography>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Box component="form" onSubmit={handleCredentialsSignIn} sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: { xs: '100%', sm: 300 } }}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
          />
          <FormControlLabel
            control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
            label={<Typography variant="body2">Remember me</Typography>}
            sx={{ alignSelf: 'flex-start' }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              mt: 1,
              fontWeight: 600,
              py: 1.25,
              backgroundColor: '#2563EB !important',
              color: '#fff !important',
              '&:hover': { backgroundColor: '#1d4ed8 !important', color: '#fff !important' },
              '& .MuiButton-startIcon, & .MuiButton-startIcon svg': { color: '#fff !important', fill: '#fff !important' },
            }}
          >
            {loading ? 'Signing in...' : 'Sign In with Email'}
          </Button>
        </Box>
        <MuiLink component={Link} href="/auth/forgot" variant="body2" sx={{ mt: 1 }}>
          Forgot password?
        </MuiLink>
        <Divider sx={{ width: '80%', maxWidth: 300, my: 3 }}>OR</Divider>
        {providers &&
          Object.values(providers).map((provider: ClientSafeProvider) => (
            <Button
              key={provider.name}
              variant="outlined"
              fullWidth
              onClick={() => signIn(provider.id)}
              sx={{
                mt: 1,
                maxWidth: 300,
              }}
            >
              Sign in with {provider.name}
            </Button>
          ))}
        <Typography variant="body2" sx={{ mt: 2 }}>
          Don&apos;t have an account?{' '}
          <MuiLink component={Link} href="/auth/signup">
            Sign up
          </MuiLink>
        </Typography>
      </Box>
    </PublicPageLayout>
  );
}
